import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/App';
import { toast } from 'sonner';
import { useTheme } from '@/contexts/ThemeContext';
import {
  Zap, Play, Pause, RefreshCw, Shield, Target, Clock, CheckCircle,
  AlertTriangle, Activity, Settings, ChevronRight, Loader2, Calendar,
  BarChart3, List, Crosshair, GitBranch, Bot, FileText, Eye,
  TrendingUp, TrendingDown, ExternalLink, Link2, ArrowRight, Server, Wrench, Network, Ticket, BookOpen, Brain, Sparkles, MessageSquare, Store, Download, Star, Users, Globe, Lock, Copy, Trash2, Package, ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, AreaChart, Area
} from 'recharts';

const RISK_COLORS = {
  critical: '#ef4444',
  high: '#f97316',
  medium: '#eab308',
  low: '#22c55e'
};

const formatNumber = (num) => {
  if (!num && num !== 0) return '0';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

export default function AutomationDashboard() {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [automationStatus, setAutomationStatus] = useState(null);
  const [rules, setRules] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [actionLog, setActionLog] = useState([]);
  const [basSimulations, setBasSimulations] = useState([]);
  const [attackPaths, setAttackPaths] = useState([]);
  const [basProfiles, setBasProfiles] = useState({});
  const [basTechniques, setBasTechniques] = useState({});
  
  // Platform integration state
  const [platformStats, setPlatformStats] = useState(null);
  const [correlationData, setCorrelationData] = useState(null);
  const [recentActions, setRecentActions] = useState([]);
  
  // Simulation dialog
  const [showSimulationDialog, setShowSimulationDialog] = useState(false);
  const [simulationTarget, setSimulationTarget] = useState('');
  const [simulationProfile, setSimulationProfile] = useState('opportunistic');
  const [runningSimulation, setRunningSimulation] = useState(false);
  const [simulationResult, setSimulationResult] = useState(null);
  
  // Technique details modal
  const [showTechniqueModal, setShowTechniqueModal] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [selectedTechnique, setSelectedTechnique] = useState(null);
  
  // Custom campaign dialog
  const [showCampaignDialog, setShowCampaignDialog] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    description: '',
    profile: 'targeted',
    techniques: [],
    targets: '',
    schedule: 'manual',
    notify_on_complete: true
  });
  const [customCampaigns, setCustomCampaigns] = useState([]);
  
  // Remediation state
  const [remediationTasks, setRemediationTasks] = useState([]);
  const [remediationStats, setRemediationStats] = useState(null);
  const [playbooks, setPlaybooks] = useState([]);
  
  // Correlation state  
  const [correlatedThreats, setCorrelatedThreats] = useState([]);
  const [correlationPolicies, setCorrelationPolicies] = useState([]);
  const [correlationStats, setCorrelationStats] = useState(null);
  
  // Configuration state
  const [automationConfig, setAutomationConfig] = useState({
    // Workflow Triggers
    triggers: {
      time_based: { enabled: true, schedule: 'daily', time: '02:00' },
      event_based: { enabled: true, on_critical_vuln: true, on_threat_detected: true, on_scan_complete: true },
      threshold_based: { enabled: false, risk_score_threshold: 80, vuln_count_threshold: 50 },
    },
    // Notification Preferences
    notifications: {
      email: { enabled: true, recipients: '', on_critical: true, on_completion: true, on_failure: true },
      slack: { enabled: false, webhook_url: '', channel: '#security-alerts' },
      teams: { enabled: false, webhook_url: '' },
    },
    // Auto-remediation Rules
    auto_remediation: {
      enabled: false,
      rules: [
        { id: 1, name: 'Block Critical IPs', trigger: 'critical_threat', action: 'firewall_block', auto_execute: false },
        { id: 2, name: 'Quarantine Infected Hosts', trigger: 'malware_detected', action: 'quarantine', auto_execute: false },
        { id: 3, name: 'Patch Critical Vulns', trigger: 'critical_vuln', action: 'auto_patch', auto_execute: false },
      ],
      require_approval: true,
      max_auto_actions_per_day: 10,
    },
    // Custom Playbooks
    playbooks: [
      { id: 1, name: 'Incident Response - Ransomware', steps: 5, last_run: null, enabled: true },
      { id: 2, name: 'Vulnerability Triage', steps: 3, last_run: null, enabled: true },
      { id: 3, name: 'Threat Hunting - APT', steps: 7, last_run: null, enabled: false },
    ],
  });
  const [configSaving, setConfigSaving] = useState(false);
  const [showAddPlaybookDialog, setShowAddPlaybookDialog] = useState(false);
  const [newPlaybook, setNewPlaybook] = useState({ name: '', description: '', steps: [] });
  const [newPlaybookStep, setNewPlaybookStep] = useState('');
  
  // Marketplace Templates
  const [marketplaceTemplates] = useState([
    {
      id: 'pci-dss-compliance',
      name: 'PCI-DSS Compliance Automation',
      description: 'Automated compliance checks and remediation workflows for PCI-DSS requirements. Includes network segmentation validation, encryption verification, and access control audits.',
      category: 'Compliance',
      rating: 4.8,
      downloads: 2340,
      author: 'Defendly Team',
      verified: true,
      tags: ['compliance', 'pci-dss', 'payment-security'],
      steps: 12,
      icon: Shield,
      color: 'indigo'
    },
    {
      id: 'ransomware-response',
      name: 'Incident Response - Ransomware',
      description: 'Complete ransomware incident response playbook with isolation procedures, backup verification, forensic collection, and recovery workflows.',
      category: 'Incident Response',
      rating: 4.9,
      downloads: 3120,
      author: 'Defendly Team',
      verified: true,
      tags: ['incident-response', 'ransomware', 'recovery'],
      steps: 15,
      icon: AlertTriangle,
      color: 'red'
    },
    {
      id: 'vuln-management',
      name: 'Vulnerability Management Workflow',
      description: 'End-to-end vulnerability management automation including discovery, prioritization using EPSS/CVSS, assignment, and remediation tracking with SLA enforcement.',
      category: 'Vulnerability Management',
      rating: 4.7,
      downloads: 1890,
      author: 'Defendly Team',
      verified: true,
      tags: ['vulnerability', 'patching', 'risk-management'],
      steps: 8,
      icon: Target,
      color: 'orange'
    },
    {
      id: 'soc-analyst-playbook',
      name: 'SOC Analyst Playbooks',
      description: 'Collection of standardized SOC playbooks for common security events including phishing, malware, brute force, and data exfiltration detection.',
      category: 'SOC Operations',
      rating: 4.6,
      downloads: 2780,
      author: 'Security Community',
      verified: false,
      tags: ['soc', 'alert-triage', 'investigation'],
      steps: 10,
      icon: Eye,
      color: 'cyan'
    },
    {
      id: 'threat-hunting-apt',
      name: 'Threat Hunting - APT Detection',
      description: 'Advanced persistent threat hunting automation with IOC correlation, lateral movement detection, and C2 communication analysis.',
      category: 'Threat Hunting',
      rating: 4.5,
      downloads: 1450,
      author: 'Threat Intel Team',
      verified: true,
      tags: ['threat-hunting', 'apt', 'ioc'],
      steps: 9,
      icon: Crosshair,
      color: 'violet'
    },
    {
      id: 'cloud-security-aws',
      name: 'Cloud Security - AWS',
      description: 'AWS security automation including misconfiguration detection, IAM policy analysis, S3 bucket security, and CloudTrail anomaly detection.',
      category: 'Cloud Security',
      rating: 4.4,
      downloads: 980,
      author: 'Cloud Security Team',
      verified: true,
      tags: ['cloud', 'aws', 'misconfigurations'],
      steps: 11,
      icon: Globe,
      color: 'emerald'
    },
    {
      id: 'identity-protection',
      name: 'Identity & Access Protection',
      description: 'Identity security automation for detecting compromised credentials, privilege escalation, and anomalous access patterns.',
      category: 'Identity Security',
      rating: 4.3,
      downloads: 760,
      author: 'Identity Security Team',
      verified: false,
      tags: ['identity', 'access-control', 'privilege'],
      steps: 7,
      icon: Users,
      color: 'blue'
    },
    {
      id: 'data-loss-prevention',
      name: 'Data Loss Prevention',
      description: 'Automated DLP workflows for sensitive data discovery, classification, and exfiltration prevention across endpoints and cloud.',
      category: 'Data Protection',
      rating: 4.6,
      downloads: 1120,
      author: 'Data Security Team',
      verified: true,
      tags: ['dlp', 'data-protection', 'classification'],
      steps: 6,
      icon: Lock,
      color: 'amber'
    },
  ]);
  const [selectedMarketplaceTemplate, setSelectedMarketplaceTemplate] = useState(null);
  const [marketplaceFilter, setMarketplaceFilter] = useState('all');
  
  // Applied/Imported Templates
  const [appliedTemplates, setAppliedTemplates] = useState([]);
  
  // Remove applied template
  const removeAppliedTemplate = (templateId) => {
    setAppliedTemplates(prev => prev.filter(t => t.id !== templateId));
    // Also remove from playbooks if it was added there
    setAutomationConfig(prev => ({
      ...prev,
      playbooks: prev.playbooks.filter(p => p.templateId !== templateId)
    }));
    toast.success('Template removed successfully');
  };

  useEffect(() => {
    fetchAllData();
    fetchPlatformStats();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [statusRes, rulesRes, auditRes, actionRes, basRes, pathsRes, profilesRes, techRes, remTasksRes, remStatsRes, playbooksRes, correlationRes, corrPoliciesRes] = await Promise.all([
        api.get('/automation/status').catch(() => ({ data: null })),
        api.get('/automation/rules').catch(() => ({ data: { rules: [] } })),
        api.get('/automation/audit-log?limit=50').catch(() => ({ data: { logs: [] } })),
        api.get('/automation/action-log?limit=50').catch(() => ({ data: { logs: [] } })),
        api.get('/bas/simulations?limit=20').catch(() => ({ data: { simulations: [] } })),
        api.get('/attack-paths').catch(() => ({ data: { paths: [] } })),
        api.get('/bas/profiles').catch(() => ({ data: { profiles: {} } })),
        api.get('/bas/techniques').catch(() => ({ data: { techniques: {} } })),
        api.get('/remediation-factory/tasks?limit=20').catch(() => ({ data: { tasks: [] } })),
        api.get('/remediation-factory/analytics').catch(() => ({ data: null })),
        api.get('/remediation-factory/playbooks').catch(() => ({ data: { playbooks: [] } })),
        api.get('/correlation/tickets?limit=20').catch(() => ({ data: { tickets: [] } })),
        api.get('/correlation/policy-config').catch(() => ({ data: { policies: [] } }))
      ]);
      
      setAutomationStatus(statusRes.data);
      setRules(rulesRes.data.rules || []);
      setAuditLog(auditRes.data.logs || []);
      setActionLog(actionRes.data.logs || []);
      setBasSimulations(basRes.data.simulations || []);
      setAttackPaths(pathsRes.data.paths || pathsRes.data || []);
      setBasProfiles(profilesRes.data.profiles || {});
      setBasTechniques(techRes.data.techniques || {});
      
      // Remediation data
      setRemediationTasks(remTasksRes.data.tasks || remTasksRes.data || []);
      setRemediationStats(remStatsRes.data);
      setPlaybooks(playbooksRes.data.playbooks || playbooksRes.data || []);
      
      // Correlation data
      setCorrelatedThreats(correlationRes.data.tickets || correlationRes.data || []);
      // Handle policy-config response which returns {config: {...}, is_custom: bool}
      const policyConfig = corrPoliciesRes.data.config || corrPoliciesRes.data.policies || [];
      setCorrelationPolicies(Array.isArray(policyConfig) ? policyConfig : Object.values(policyConfig || {}));
      
      // Process recent actions for activity feed
      const actions = actionRes.data.logs || [];
      setRecentActions(actions.slice(0, 10));
    } catch (error) {
      console.error('Failed to fetch automation data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlatformStats = async () => {
    try {
      const [vulnsRes, correlationRes, dashboardRes] = await Promise.all([
        api.get('/vulnerabilities').catch(() => ({ data: [] })),
        api.get('/correlation/dashboard').catch(() => ({ data: null })),
        api.get('/dashboard').catch(() => ({ data: {} }))
      ]);
      
      const vulns = vulnsRes.data || [];
      const criticalVulns = vulns.filter(v => v.severity === 'critical' || v.risk_score >= 80);
      const highRiskVulns = vulns.filter(v => v.severity === 'high' || (v.risk_score >= 60 && v.risk_score < 80));
      
      setPlatformStats({
        totalVulnerabilities: vulns.length,
        criticalVulnerabilities: criticalVulns.length,
        highRiskVulnerabilities: highRiskVulns.length,
        openTickets: correlationRes.data?.ticket_metrics?.open_tickets || 0,
        avgRiskScore: correlationRes.data?.risk_metrics?.average_risk_score || 0,
        slaCompliance: correlationRes.data?.ticket_metrics?.sla_compliance || 0
      });
      
      setCorrelationData(correlationRes.data);
    } catch (error) {
      console.error('Failed to fetch platform stats:', error);
    }
  };

  const handleStartAutomation = async () => {
    try {
      await api.post('/automation/start');
      toast.success('Automation started');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to start automation');
    }
  };

  const handleStopAutomation = async () => {
    try {
      await api.post('/automation/stop');
      toast.success('Automation stopped');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to stop automation');
    }
  };

  const handleRunSimulation = async () => {
    if (!simulationTarget) {
      toast.error('Please enter a target');
      return;
    }
    
    try {
      setRunningSimulation(true);
      const response = await api.post('/bas/simulate', {
        target: simulationTarget,
        profile: simulationProfile,
        mode: 'safe'
      });
      setSimulationResult(response.data);
      toast.success('Simulation completed');
      fetchAllData();
    } catch (error) {
      toast.error('Simulation failed');
    } finally {
      setRunningSimulation(false);
    }
  };

  const handleAnalyzeAttackPaths = async () => {
    try {
      setLoading(true);
      await api.post('/attack-paths/analyze');
      toast.success('Attack path analysis completed');
      fetchAllData();
    } catch (error) {
      toast.error('Attack path analysis failed');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !automationStatus) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  const scheduleData = automationStatus?.scan_schedule?.schedules || [];
  
  // Chart data for activity trends
  const activityTrendData = [
    { name: 'Mon', events: 12, actions: 8 },
    { name: 'Tue', events: 19, actions: 14 },
    { name: 'Wed', events: 15, actions: 11 },
    { name: 'Thu', events: 22, actions: 17 },
    { name: 'Fri', events: 18, actions: 13 },
    { name: 'Sat', events: 8, actions: 5 },
    { name: 'Sun', events: 6, actions: 4 },
  ];
  
  // Automation distribution data
  const automationDistribution = [
    { name: 'WAF Rules', value: 35, color: '#8b5cf6' },
    { name: 'IDS Updates', value: 25, color: '#06b6d4' },
    { name: 'Firewall', value: 20, color: '#22c55e' },
    { name: 'Tickets', value: 15, color: '#f97316' },
    { name: 'Alerts', value: 5, color: '#ef4444' },
  ];

  return (
    <div className="p-3 sm:p-4 lg:p-6 space-y-4 sm:space-y-6" data-testid="automation-dashboard">
      {/* Header - Mobile Responsive */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-xl ${isDarkMode ? 'bg-violet-500/20' : 'bg-violet-100'}`}>
            <Bot className="w-5 sm:w-6 h-5 sm:h-6 text-violet-500" />
          </div>
          <div>
            <h1 className={`text-xl sm:text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Automation Center
            </h1>
            <p className={`text-xs sm:text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
              CTEM Automation, BAS, and Attack Path Analysis
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {automationStatus?.running ? (
            <Button variant="outline" onClick={handleStopAutomation} className="text-red-500 text-xs sm:text-sm" size="sm">
              <Pause className="w-3 sm:w-4 h-3 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden xs:inline">Stop</span> Automation
            </Button>
          ) : (
            <Button onClick={handleStartAutomation} className="bg-green-600 hover:bg-green-500 text-xs sm:text-sm" size="sm">
              <Play className="w-3 sm:w-4 h-3 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden xs:inline">Start</span> Automation
            </Button>
          )}
          <Button variant="outline" onClick={fetchAllData} size="sm" className="text-xs sm:text-sm">
            <RefreshCw className="w-3 sm:w-4 h-3 sm:h-4 sm:mr-2" />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
        </div>
      </div>

      {/* Status Cards - Mobile Optimized Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 lg:gap-4">
        <StatusCard
          title="Orchestrator"
          value={automationStatus?.running ? 'Running' : 'Stopped'}
          icon={<Zap className="w-4 sm:w-5 h-4 sm:h-5" />}
          color={automationStatus?.running ? 'green' : 'red'}
          isDarkMode={isDarkMode}
        />
        <StatusCard
          title="Event Bus"
          value={automationStatus?.event_bus_running ? 'Active' : 'Inactive'}
          icon={<Activity className="w-4 sm:w-5 h-4 sm:h-5" />}
          color={automationStatus?.event_bus_running ? 'green' : 'red'}
          isDarkMode={isDarkMode}
        />
        <StatusCard
          title="Scan Scheduler"
          value={automationStatus?.scan_scheduler_enabled ? 'Enabled' : 'Disabled'}
          icon={<Clock className="w-4 sm:w-5 h-4 sm:h-5" />}
          color={automationStatus?.scan_scheduler_enabled ? 'green' : 'red'}
          isDarkMode={isDarkMode}
        />
        <StatusCard
          title="Active Rules"
          value={automationStatus?.rules_count || 0}
          icon={<Settings className="w-4 sm:w-5 h-4 sm:h-5" />}
          color="violet"
          isDarkMode={isDarkMode}
        />
        <StatusCard
          title="Tier 1 Actions"
          value={automationStatus?.tier1_actions_enabled?.length || 0}
          icon={<Shield className="w-4 sm:w-5 h-4 sm:h-5" />}
          color="blue"
          isDarkMode={isDarkMode}
        />
      </div>
      
      {/* Platform Integration Panel - Shows correlation with other modules */}
      {platformStats && (
        <div className={`rounded-xl p-4 sm:p-6 ${isDarkMode ? 'bg-gradient-to-r from-violet-500/10 to-cyan-500/10 border border-violet-500/20' : 'bg-gradient-to-r from-violet-50 to-cyan-50 border border-violet-200'}`}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
            <div className="flex items-center gap-2">
              <Link2 className={`w-5 h-5 ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`} />
              <h3 className={`text-base sm:text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Platform Integration Status
              </h3>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/correlation-engine')}
              className="text-xs sm:text-sm"
            >
              <ExternalLink className="w-3 h-3 mr-1" />
              View Correlation Engine
            </Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-4">
            <IntegrationMetric
              label="Critical Vulns"
              value={formatNumber(platformStats.criticalVulnerabilities)}
              trend={platformStats.criticalVulnerabilities > 0 ? 'up' : 'down'}
              isDarkMode={isDarkMode}
              onClick={() => navigate('/vulnerability-hub', { state: { filter: { severity: 'critical' } } })}
            />
            <IntegrationMetric
              label="High Risk"
              value={formatNumber(platformStats.highRiskVulnerabilities)}
              trend={platformStats.highRiskVulnerabilities > 5 ? 'up' : 'down'}
              isDarkMode={isDarkMode}
              onClick={() => navigate('/vulnerability-hub', { state: { filter: { severity: 'high' } } })}
            />
            <IntegrationMetric
              label="Open Tickets"
              value={formatNumber(platformStats.openTickets)}
              trend={platformStats.openTickets > 10 ? 'up' : 'down'}
              isDarkMode={isDarkMode}
              onClick={() => navigate('/correlation-engine')}
            />
            <IntegrationMetric
              label="Avg Risk Score"
              value={platformStats.avgRiskScore?.toFixed(1) || '0'}
              trend={platformStats.avgRiskScore > 50 ? 'up' : 'down'}
              isDarkMode={isDarkMode}
            />
            <IntegrationMetric
              label="SLA Compliance"
              value={`${platformStats.slaCompliance || 0}%`}
              trend={platformStats.slaCompliance >= 90 ? 'down' : 'up'}
              isDarkMode={isDarkMode}
            />
            <IntegrationMetric
              label="Attack Paths"
              value={formatNumber(attackPaths.length)}
              trend={attackPaths.length > 5 ? 'up' : 'down'}
              isDarkMode={isDarkMode}
            />
          </div>
        </div>
      )}

      {/* Tabs - Mobile Scrollable */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0">
          <TabsList className={`${isDarkMode ? 'bg-zinc-800' : 'bg-slate-100'} min-w-max`}>
            <TabsTrigger value="overview" data-testid="automation-overview-tab" className="text-xs sm:text-sm">
              <BarChart3 className="w-3 sm:w-4 h-3 sm:h-4 mr-1 sm:mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="remediation" data-testid="automation-remediation-tab" className="text-xs sm:text-sm">
              <Wrench className="w-3 sm:w-4 h-3 sm:h-4 mr-1 sm:mr-2" />
              Remediation
            </TabsTrigger>
            <TabsTrigger value="correlation" data-testid="automation-correlation-tab" className="text-xs sm:text-sm">
              <Network className="w-3 sm:w-4 h-3 sm:h-4 mr-1 sm:mr-2" />
              Correlation
            </TabsTrigger>
            <TabsTrigger value="attack-paths" data-testid="automation-paths-tab" className="text-xs sm:text-sm">
              <GitBranch className="w-3 sm:w-4 h-3 sm:h-4 mr-1 sm:mr-2" />
              Attack Paths
            </TabsTrigger>
            <TabsTrigger value="schedule" data-testid="automation-schedule-tab" className="text-xs sm:text-sm">
              <Calendar className="w-3 sm:w-4 h-3 sm:h-4 mr-1 sm:mr-2" />
              Schedule
            </TabsTrigger>
            <TabsTrigger value="logs" data-testid="automation-logs-tab" className="text-xs sm:text-sm">
              <FileText className="w-3 sm:w-4 h-3 sm:h-4 mr-1 sm:mr-2" />
              Logs
            </TabsTrigger>
            <TabsTrigger value="configuration" data-testid="automation-config-tab" className="text-xs sm:text-sm">
              <Settings className="w-3 sm:w-4 h-3 sm:h-4 mr-1 sm:mr-2" />
              Configuration
            </TabsTrigger>
            <TabsTrigger value="marketplace" data-testid="automation-marketplace-tab" className="text-xs sm:text-sm">
              <Store className="w-3 sm:w-4 h-3 sm:h-4 mr-1 sm:mr-2" />
              Marketplace
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Overview Tab - Enhanced with charts */}
        <TabsContent value="overview" className="space-y-4 sm:space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Automation Rules */}
            <div className={`rounded-xl p-4 sm:p-6 ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-white border border-slate-200 shadow-sm'}`}>
              <h3 className={`text-base sm:text-lg font-semibold mb-3 sm:mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Automation Rules
              </h3>
              <div className="space-y-2 sm:space-y-3 max-h-64 sm:max-h-80 overflow-y-auto">
                {rules.length === 0 ? (
                  <p className={`text-center py-4 text-sm ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                    No automation rules configured
                  </p>
                ) : (
                  rules.map((rule, idx) => (
                    <div key={rule.id || idx} className={`p-2 sm:p-3 rounded-lg flex items-center justify-between ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-50'}`}>
                      <div className="min-w-0 flex-1">
                        <p className={`font-medium text-sm sm:text-base truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{rule.name}</p>
                        <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                          Trigger: {rule.trigger_event?.replace(/_/g, ' ')}
                        </p>
                      </div>
                      <Badge variant={rule.enabled ? "default" : "secondary"} className={`ml-2 ${rule.enabled ? 'bg-green-500' : ''} text-xs`}>
                        {rule.enabled ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Tier 1 Auto-Remediations */}
            <div className={`rounded-xl p-4 sm:p-6 ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-white border border-slate-200 shadow-sm'}`}>
              <h3 className={`text-base sm:text-lg font-semibold mb-2 sm:mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Tier 1 Auto-Remediations
              </h3>
              <p className={`text-xs sm:text-sm mb-3 sm:mb-4 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                Actions that execute automatically without human approval
              </p>
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {automationStatus?.tier1_actions_enabled?.map((action) => (
                  <Badge key={action} variant="outline" className={`text-xs ${isDarkMode ? 'border-green-500/50 text-green-400' : 'border-green-300 text-green-600'}`}>
                    <CheckCircle className="w-2.5 sm:w-3 h-2.5 sm:h-3 mr-1" />
                    {action.replace(/_/g, ' ')}
                  </Badge>
                )) || (
                  <p className={`text-sm ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                    No Tier 1 actions enabled
                  </p>
                )}
              </div>
            </div>
          </div>
          
          {/* Activity Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Activity Trend Chart */}
            <div className={`rounded-xl p-4 sm:p-6 ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-white border border-slate-200 shadow-sm'}`}>
              <h3 className={`text-base sm:text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Weekly Activity Trend
              </h3>
              <div className="h-48 sm:h-64">
                <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={150}>
                  <AreaChart data={activityTrendData}>
                    <defs>
                      <linearGradient id="colorEvents" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorActions" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#27272a' : '#e2e8f0'} />
                    <XAxis dataKey="name" stroke={isDarkMode ? '#71717a' : '#94a3b8'} fontSize={10} />
                    <YAxis stroke={isDarkMode ? '#71717a' : '#94a3b8'} fontSize={10} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: isDarkMode ? '#18181b' : '#fff',
                        border: isDarkMode ? '1px solid #3f3f46' : '1px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '12px'
                      }}
                    />
                    <Area type="monotone" dataKey="events" stroke="#8b5cf6" fill="url(#colorEvents)" name="Events" />
                    <Area type="monotone" dataKey="actions" stroke="#22c55e" fill="url(#colorActions)" name="Actions" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            {/* Automation Distribution */}
            <div className={`rounded-xl p-4 sm:p-6 ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-white border border-slate-200 shadow-sm'}`}>
              <h3 className={`text-base sm:text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Automation Distribution
              </h3>
              <div className="h-48 sm:h-64 flex items-center">
                <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={150}>
                  <PieChart>
                    <Pie
                      data={automationDistribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {automationDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: isDarkMode ? '#18181b' : '#fff',
                        border: isDarkMode ? '1px solid #3f3f46' : '1px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '12px'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-col gap-1.5 ml-2 text-xs">
                  {automationDistribution.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                      <span className={isDarkMode ? 'text-zinc-400' : 'text-slate-600'}>{item.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          {/* Recent Activity Feed */}
          <div className={`rounded-xl p-4 sm:p-6 ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-white border border-slate-200 shadow-sm'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-base sm:text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Recent Automation Activity
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setActiveTab('logs')} className="text-xs">
                View All <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
            <div className="space-y-2">
              {recentActions.length === 0 ? (
                <p className={`text-center py-4 text-sm ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                  No recent activity
                </p>
              ) : (
                recentActions.slice(0, 5).map((action, idx) => (
                  <div key={action.action_id || idx} className={`flex items-center gap-3 p-2 sm:p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-50'}`}>
                    <div className={`p-1.5 rounded-lg ${action.result?.success ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                      {action.result?.success ? (
                        <CheckCircle className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-green-500" />
                      ) : (
                        <AlertTriangle className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-red-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs sm:text-sm font-medium truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {action.action_type?.replace(/_/g, ' ')}
                      </p>
                      <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                        {new Date(action.executed_at).toLocaleString()}
                      </p>
                    </div>
                    <Badge variant="outline" className="text-xs hidden sm:flex">
                      {action.result?.success ? 'Success' : 'Failed'}
                    </Badge>
                  </div>
                ))
              )}
            </div>
          </div>
        </TabsContent>

        {/* Remediation Tab */}
        <TabsContent value="remediation" className="space-y-4 sm:space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <h3 className={`text-base sm:text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Remediation Factory
            </h3>
            <Button 
              onClick={() => navigate('/remediation-factory')}
              variant="outline"
              size="sm"
              className="text-xs sm:text-sm"
            >
              <ExternalLink className="w-3 sm:w-4 h-3 sm:h-4 mr-1 sm:mr-2" />
              Full View
            </Button>
          </div>

          {/* Remediation Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            {[
              { label: 'Total Tasks', value: remediationStats?.total_tasks || remediationTasks.length || 0, color: 'violet', icon: Ticket },
              { label: 'In Progress', value: remediationStats?.in_progress || remediationTasks.filter(t => t.status === 'in_progress').length || 0, color: 'blue', icon: Loader2 },
              { label: 'Completed', value: remediationStats?.completed || remediationTasks.filter(t => t.status === 'completed').length || 0, color: 'emerald', icon: CheckCircle },
              { label: 'Playbooks', value: playbooks.length || 0, color: 'orange', icon: BookOpen }
            ].map((stat) => (
              <div key={stat.label} className={`rounded-xl p-3 sm:p-4 ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-white border border-slate-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <stat.icon className={`w-4 h-4 text-${stat.color}-500`} />
                  <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>{stat.label}</span>
                </div>
                <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{stat.value}</p>
              </div>
            ))}
          </div>

          {/* Recent Remediation Tasks */}
          <div className={`rounded-xl p-4 sm:p-6 ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-white border border-slate-200 shadow-sm'}`}>
            <h4 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Recent Remediation Tasks</h4>
            {remediationTasks.length === 0 ? (
              <div className={`text-center py-8 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                <Wrench className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No remediation tasks yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {remediationTasks.slice(0, 5).map((task, idx) => (
                  <div key={task.id || idx} className={`p-3 rounded-lg flex items-center justify-between ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-50'}`}>
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{task.title || task.vulnerability_title || 'Task'}</p>
                      <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                        {task.priority && <span className="capitalize">{task.priority} Priority</span>}
                        {task.assignee && <span className="ml-2">• Assigned to {task.assignee}</span>}
                      </p>
                    </div>
                    <Badge className={`text-xs capitalize ${
                      task.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                      task.status === 'in_progress' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {task.status?.replace(/_/g, ' ') || 'pending'}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Available Playbooks */}
          <div className={`rounded-xl p-4 sm:p-6 ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-white border border-slate-200 shadow-sm'}`}>
            <h4 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Remediation Playbooks</h4>
            {playbooks.length === 0 ? (
              <div className={`text-center py-8 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No playbooks available</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {playbooks.slice(0, 6).map((playbook, idx) => (
                  <div key={playbook.id || idx} className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-50'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <BookOpen className="w-4 h-4 text-orange-500" />
                      <span className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{playbook.name || playbook.title || 'Playbook'}</span>
                    </div>
                    <p className={`text-xs line-clamp-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>{playbook.description || 'No description'}</p>
                    <p className={`text-xs mt-2 ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`}>
                      {playbook.steps?.length || 0} steps
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Correlation Tab */}
        <TabsContent value="correlation" className="space-y-4 sm:space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <h3 className={`text-base sm:text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Correlation Engine
            </h3>
            <Button 
              onClick={() => navigate('/correlation-engine')}
              variant="outline"
              size="sm"
              className="text-xs sm:text-sm"
            >
              <ExternalLink className="w-3 sm:w-4 h-3 sm:h-4 mr-1 sm:mr-2" />
              Full View
            </Button>
          </div>

          {/* Correlation Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            {[
              { label: 'Correlated Threats', value: correlatedThreats.length || 0, color: 'red', icon: AlertTriangle },
              { label: 'Active Policies', value: correlationPolicies.filter(p => p.enabled).length || 0, color: 'emerald', icon: Shield },
              { label: 'Total Policies', value: correlationPolicies.length || 0, color: 'violet', icon: Network },
              { label: 'Auto-Actions', value: correlationPolicies.filter(p => p.auto_remediate).length || 0, color: 'blue', icon: Zap }
            ].map((stat) => (
              <div key={stat.label} className={`rounded-xl p-3 sm:p-4 ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-white border border-slate-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <stat.icon className={`w-4 h-4 text-${stat.color}-500`} />
                  <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>{stat.label}</span>
                </div>
                <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{stat.value}</p>
              </div>
            ))}
          </div>

          {/* Correlated Threats */}
          <div className={`rounded-xl p-4 sm:p-6 ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-white border border-slate-200 shadow-sm'}`}>
            <h4 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Recent Correlated Threats</h4>
            {correlatedThreats.length === 0 ? (
              <div className={`text-center py-8 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                <Network className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No correlated threats detected</p>
              </div>
            ) : (
              <div className="space-y-3">
                {correlatedThreats.slice(0, 5).map((threat, idx) => (
                  <div key={threat.id || idx} className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-50'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{threat.title || threat.name || 'Threat'}</span>
                      <Badge className={`text-xs capitalize ${
                        threat.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                        threat.severity === 'high' ? 'bg-orange-500/20 text-orange-400' :
                        threat.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-green-500/20 text-green-400'
                      }`}>
                        {threat.severity || 'medium'}
                      </Badge>
                    </div>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                      {threat.correlated_events || 0} correlated events • 
                      {threat.affected_assets || 0} affected assets
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Correlation Policies */}
          <div className={`rounded-xl p-4 sm:p-6 ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-white border border-slate-200 shadow-sm'}`}>
            <h4 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Correlation Policies</h4>
            {correlationPolicies.length === 0 ? (
              <div className={`text-center py-8 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                <Shield className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No correlation policies configured</p>
              </div>
            ) : (
              <div className="space-y-3">
                {correlationPolicies.slice(0, 5).map((policy, idx) => (
                  <div key={policy.id || idx} className={`p-3 rounded-lg flex items-center justify-between ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-50'}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${policy.enabled ? 'bg-emerald-500' : 'bg-zinc-500'}`} />
                        <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{policy.name || 'Policy'}</span>
                      </div>
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                        {policy.condition_type || policy.description || 'No description'}
                      </p>
                    </div>
                    {policy.auto_remediate && (
                      <Badge className="text-xs bg-violet-500/20 text-violet-400">
                        <Zap className="w-3 h-3 mr-1" />
                        Auto
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Attack Paths Tab - Mobile Responsive */}
        <TabsContent value="attack-paths" className="space-y-4 sm:space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <h3 className={`text-base sm:text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Attack Path Analysis
            </h3>
            <Button onClick={handleAnalyzeAttackPaths} data-testid="analyze-attack-paths-btn" size="sm" className="text-xs sm:text-sm">
              <GitBranch className="w-3 sm:w-4 h-3 sm:h-4 mr-1 sm:mr-2" />
              Analyze Paths
            </Button>
          </div>

          {attackPaths.length === 0 ? (
            <div className={`rounded-xl p-8 sm:p-12 text-center ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-white border border-slate-200'}`}>
              <GitBranch className={`w-10 sm:w-12 h-10 sm:h-12 mx-auto mb-3 sm:mb-4 ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`} />
              <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                No attack paths found.
              </p>
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>
                Click "Analyze Paths" to discover potential attack vectors.
              </p>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              {attackPaths.map((path) => (
                <div key={path.id} className={`rounded-xl p-4 sm:p-5 ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-white border border-slate-200 shadow-sm'}`}>
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h4 className={`font-semibold text-sm sm:text-base ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{path.name}</h4>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 mt-2">
                        <span className={`text-xs sm:text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                          Entry: <span className="text-cyan-500">{path.entry_point}</span>
                        </span>
                        <ChevronRight className={`w-4 h-4 hidden sm:block ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`} />
                        <span className={`text-xs sm:text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                          Target: <span className="text-red-500">{path.target}</span>
                        </span>
                      </div>
                    </div>
                    <div className="flex sm:flex-col items-center sm:items-end gap-2 sm:gap-0">
                      <Badge className={`text-xs ${
                        path.likelihood >= 70 ? 'bg-red-500' :
                        path.likelihood >= 50 ? 'bg-orange-500' : 'bg-yellow-500'
                      } text-white`}>
                        {path.likelihood}% likely
                      </Badge>
                      <p className={`text-xs sm:mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                        Impact: <span className="capitalize">{path.impact}</span>
                      </p>
                    </div>
                  </div>
                  {path.description && (
                    <p className={`text-xs sm:text-sm mt-3 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                      {path.description}
                    </p>
                  )}
                  {path.techniques?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-3">
                      {path.techniques.map((tech) => (
                        <Badge key={tech} variant="outline" className={`text-xs ${isDarkMode ? 'border-violet-500/50 text-violet-400' : ''}`}>
                          {tech}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Schedule Tab - Mobile Responsive */}
        <TabsContent value="schedule" className="space-y-4 sm:space-y-6">
          <h3 className={`text-base sm:text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            Continuous Scan Schedule
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {scheduleData.length === 0 ? (
              <div className={`col-span-full rounded-xl p-8 text-center ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-white border border-slate-200'}`}>
                <Calendar className={`w-10 h-10 mx-auto mb-3 ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`} />
                <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                  No scans scheduled yet.
                </p>
              </div>
            ) : (
              scheduleData.map((schedule) => (
                <div key={schedule.job_id} className={`rounded-xl p-3 sm:p-4 ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-white border border-slate-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className={`font-medium text-sm sm:text-base capitalize ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {schedule.scan_type?.replace(/_/g, ' ')}
                    </h4>
                    <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/30 text-xs">
                      Active
                    </Badge>
                  </div>
                  <p className={`text-xs sm:text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                    Interval: {schedule.interval}
                  </p>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>
                    Next: {new Date(schedule.next_run).toLocaleString()}
                  </p>
                </div>
              ))
            )}
          </div>
        </TabsContent>

        {/* Logs Tab - Mobile Responsive */}
        <TabsContent value="logs" className="space-y-4 sm:space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Audit Log */}
            <div className={`rounded-xl p-4 sm:p-6 ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-white border border-slate-200 shadow-sm'}`}>
              <h3 className={`text-base sm:text-lg font-semibold mb-3 sm:mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Event Audit Log
              </h3>
              <div className="space-y-2 max-h-64 sm:max-h-96 overflow-y-auto">
                {auditLog.length === 0 ? (
                  <p className={`text-center py-4 text-sm ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>No events logged</p>
                ) : (
                  auditLog.map((log, idx) => (
                    <div key={log.event_id || idx} className={`p-2 sm:p-3 rounded-lg text-xs sm:text-sm ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-50'}`}>
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-2">
                        <Badge variant="outline" className="text-xs w-fit">{log.event_type?.replace(/_/g, ' ')}</Badge>
                        <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                          {new Date(log.timestamp).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Action Log */}
            <div className={`rounded-xl p-4 sm:p-6 ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-white border border-slate-200 shadow-sm'}`}>
              <h3 className={`text-base sm:text-lg font-semibold mb-3 sm:mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Action Execution Log
              </h3>
              <div className="space-y-2 max-h-64 sm:max-h-96 overflow-y-auto">
                {actionLog.length === 0 ? (
                  <p className={`text-center py-4 text-sm ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>No actions executed</p>
                ) : (
                  actionLog.map((log, idx) => (
                    <div key={log.action_id || idx} className={`p-2 sm:p-3 rounded-lg text-xs sm:text-sm ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-50'}`}>
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">{log.action_type?.replace(/_/g, ' ')}</Badge>
                          {log.result?.success ? (
                            <CheckCircle className="w-3 sm:w-4 h-3 sm:h-4 text-green-500" />
                          ) : (
                            <AlertTriangle className="w-3 sm:w-4 h-3 sm:h-4 text-red-500" />
                          )}
                        </div>
                        <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                          {new Date(log.executed_at).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </TabsContent>
        
        {/* Configuration Tab */}
        <TabsContent value="configuration" className="space-y-4 sm:space-y-6">
          {/* Header */}
          <div className={`rounded-xl p-4 sm:p-6 ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-white border border-slate-200 shadow-sm'}`}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-violet-500/20 border border-violet-500/30' : 'bg-violet-100 border border-violet-200'}`}>
                  <Settings className={`w-5 h-5 ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`} />
                </div>
                <div>
                  <h2 className={`text-base sm:text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Automation Configuration</h2>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Configure workflow triggers, notifications, and auto-remediation rules</p>
                </div>
              </div>
              <Button 
                onClick={() => {
                  setConfigSaving(true);
                  setTimeout(() => {
                    setConfigSaving(false);
                    toast.success('Configuration saved successfully');
                  }, 1000);
                }}
                disabled={configSaving}
                className="bg-violet-600 hover:bg-violet-500 w-full sm:w-auto"
                data-testid="save-automation-config-btn"
              >
                {configSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                <span className="hidden sm:inline">Save Configuration</span>
                <span className="sm:hidden">Save</span>
              </Button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Workflow Triggers */}
            <div className={`rounded-xl p-4 sm:p-6 ${isDarkMode ? 'bg-zinc-900/50 border border-cyan-500/20' : 'bg-white border border-cyan-200 shadow-sm'}`}>
              <div className="flex items-center gap-2 mb-4">
                <Clock className={`w-5 h-5 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
                <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Workflow Triggers</h3>
              </div>
              
              <div className="space-y-4">
                {/* Time-based */}
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Time-based Triggers</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={automationConfig.triggers.time_based.enabled}
                        onChange={(e) => setAutomationConfig(prev => ({
                          ...prev,
                          triggers: { ...prev.triggers, time_based: { ...prev.triggers.time_based, enabled: e.target.checked }}
                        }))}
                        className="sr-only peer"
                        data-testid="time-trigger-toggle"
                      />
                      <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-cyan-500"></div>
                    </label>
                  </div>
                  {automationConfig.triggers.time_based.enabled && (
                    <div className="flex items-center gap-2 mt-2">
                      <Select 
                        value={automationConfig.triggers.time_based.schedule}
                        onValueChange={(v) => setAutomationConfig(prev => ({
                          ...prev,
                          triggers: { ...prev.triggers, time_based: { ...prev.triggers.time_based, schedule: v }}
                        }))}
                      >
                        <SelectTrigger className={`w-32 text-sm ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}>
                          <SelectItem value="hourly">Hourly</SelectItem>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                      <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>at</span>
                      <Input 
                        type="time"
                        value={automationConfig.triggers.time_based.time}
                        onChange={(e) => setAutomationConfig(prev => ({
                          ...prev,
                          triggers: { ...prev.triggers, time_based: { ...prev.triggers.time_based, time: e.target.value }}
                        }))}
                        className={`w-24 text-sm ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}`}
                      />
                    </div>
                  )}
                </div>
                
                {/* Event-based */}
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Event-based Triggers</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={automationConfig.triggers.event_based.enabled}
                        onChange={(e) => setAutomationConfig(prev => ({
                          ...prev,
                          triggers: { ...prev.triggers, event_based: { ...prev.triggers.event_based, enabled: e.target.checked }}
                        }))}
                        className="sr-only peer"
                        data-testid="event-trigger-toggle"
                      />
                      <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-cyan-500"></div>
                    </label>
                  </div>
                  {automationConfig.triggers.event_based.enabled && (
                    <div className="space-y-2 mt-2">
                      {[
                        { key: 'on_critical_vuln', label: 'On Critical Vulnerability' },
                        { key: 'on_threat_detected', label: 'On Threat Detected' },
                        { key: 'on_scan_complete', label: 'On Scan Complete' },
                      ].map(item => (
                        <label key={item.key} className="flex items-center gap-2 cursor-pointer">
                          <input 
                            type="checkbox"
                            checked={automationConfig.triggers.event_based[item.key]}
                            onChange={(e) => setAutomationConfig(prev => ({
                              ...prev,
                              triggers: { ...prev.triggers, event_based: { ...prev.triggers.event_based, [item.key]: e.target.checked }}
                            }))}
                            className="w-4 h-4 rounded border-gray-300 text-cyan-500 focus:ring-cyan-500"
                          />
                          <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>{item.label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Notification Preferences */}
            <div className={`rounded-xl p-4 sm:p-6 ${isDarkMode ? 'bg-zinc-900/50 border border-emerald-500/20' : 'bg-white border border-emerald-200 shadow-sm'}`}>
              <div className="flex items-center gap-2 mb-4">
                <MessageSquare className={`w-5 h-5 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Notification Preferences</h3>
              </div>
              
              <div className="space-y-4">
                {/* Email */}
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Email Notifications</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={automationConfig.notifications.email.enabled}
                        onChange={(e) => setAutomationConfig(prev => ({
                          ...prev,
                          notifications: { ...prev.notifications, email: { ...prev.notifications.email, enabled: e.target.checked }}
                        }))}
                        className="sr-only peer"
                        data-testid="email-notif-toggle"
                      />
                      <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-emerald-500"></div>
                    </label>
                  </div>
                  {automationConfig.notifications.email.enabled && (
                    <Input 
                      placeholder="Recipients (comma-separated)"
                      value={automationConfig.notifications.email.recipients}
                      onChange={(e) => setAutomationConfig(prev => ({
                        ...prev,
                        notifications: { ...prev.notifications, email: { ...prev.notifications.email, recipients: e.target.value }}
                      }))}
                      className={`mt-2 text-sm ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}`}
                      data-testid="email-recipients-input"
                    />
                  )}
                </div>
                
                {/* Slack */}
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Slack Integration</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={automationConfig.notifications.slack.enabled}
                        onChange={(e) => setAutomationConfig(prev => ({
                          ...prev,
                          notifications: { ...prev.notifications, slack: { ...prev.notifications.slack, enabled: e.target.checked }}
                        }))}
                        className="sr-only peer"
                        data-testid="slack-notif-toggle"
                      />
                      <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-emerald-500"></div>
                    </label>
                  </div>
                  {automationConfig.notifications.slack.enabled && (
                    <div className="space-y-2 mt-2">
                      <Input 
                        placeholder="Webhook URL"
                        value={automationConfig.notifications.slack.webhook_url}
                        onChange={(e) => setAutomationConfig(prev => ({
                          ...prev,
                          notifications: { ...prev.notifications, slack: { ...prev.notifications.slack, webhook_url: e.target.value }}
                        }))}
                        className={`text-sm ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}`}
                        data-testid="slack-webhook-input"
                      />
                      <Input 
                        placeholder="Channel (e.g., #security-alerts)"
                        value={automationConfig.notifications.slack.channel}
                        onChange={(e) => setAutomationConfig(prev => ({
                          ...prev,
                          notifications: { ...prev.notifications, slack: { ...prev.notifications.slack, channel: e.target.value }}
                        }))}
                        className={`text-sm ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}`}
                      />
                    </div>
                  )}
                </div>
                
                {/* Teams */}
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Microsoft Teams</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={automationConfig.notifications.teams.enabled}
                        onChange={(e) => setAutomationConfig(prev => ({
                          ...prev,
                          notifications: { ...prev.notifications, teams: { ...prev.notifications.teams, enabled: e.target.checked }}
                        }))}
                        className="sr-only peer"
                        data-testid="teams-notif-toggle"
                      />
                      <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-emerald-500"></div>
                    </label>
                  </div>
                  {automationConfig.notifications.teams.enabled && (
                    <Input 
                      placeholder="Webhook URL"
                      value={automationConfig.notifications.teams.webhook_url}
                      onChange={(e) => setAutomationConfig(prev => ({
                        ...prev,
                        notifications: { ...prev.notifications, teams: { ...prev.notifications.teams, webhook_url: e.target.value }}
                      }))}
                      className={`mt-2 text-sm ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}`}
                      data-testid="teams-webhook-input"
                    />
                  )}
                </div>
              </div>
            </div>
            
            {/* Auto-Remediation Rules */}
            <div className={`rounded-xl p-4 sm:p-6 ${isDarkMode ? 'bg-zinc-900/50 border border-orange-500/20' : 'bg-white border border-orange-200 shadow-sm'}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Zap className={`w-5 h-5 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} />
                  <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Auto-Remediation Rules</h3>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={automationConfig.auto_remediation.enabled}
                    onChange={(e) => setAutomationConfig(prev => ({
                      ...prev,
                      auto_remediation: { ...prev.auto_remediation, enabled: e.target.checked }
                    }))}
                    className="sr-only peer"
                    data-testid="auto-remediation-toggle"
                  />
                  <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-orange-500"></div>
                </label>
              </div>
              
              {automationConfig.auto_remediation.enabled && (
                <div className="space-y-3">
                  {automationConfig.auto_remediation.rules.map((rule) => (
                    <div key={rule.id} className={`p-3 rounded-lg flex items-center justify-between ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                      <div>
                        <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{rule.name}</p>
                        <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                          Trigger: {rule.trigger.replace('_', ' ')} → Action: {rule.action.replace('_', ' ')}
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={rule.auto_execute}
                          onChange={(e) => {
                            const newRules = automationConfig.auto_remediation.rules.map(r => 
                              r.id === rule.id ? { ...r, auto_execute: e.target.checked } : r
                            );
                            setAutomationConfig(prev => ({
                              ...prev,
                              auto_remediation: { ...prev.auto_remediation, rules: newRules }
                            }));
                          }}
                          className="sr-only peer"
                        />
                        <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-orange-500"></div>
                      </label>
                    </div>
                  ))}
                  
                  <div className="flex items-center gap-4 pt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="checkbox"
                        checked={automationConfig.auto_remediation.require_approval}
                        onChange={(e) => setAutomationConfig(prev => ({
                          ...prev,
                          auto_remediation: { ...prev.auto_remediation, require_approval: e.target.checked }
                        }))}
                        className="w-4 h-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                        data-testid="require-approval-checkbox"
                      />
                      <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>Require approval before execution</span>
                    </label>
                  </div>
                  
                  <div className="flex items-center gap-2 pt-2">
                    <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Max auto-actions per day:</span>
                    <Input 
                      type="number"
                      min="1"
                      max="100"
                      value={automationConfig.auto_remediation.max_auto_actions_per_day}
                      onChange={(e) => setAutomationConfig(prev => ({
                        ...prev,
                        auto_remediation: { ...prev.auto_remediation, max_auto_actions_per_day: parseInt(e.target.value) || 10 }
                      }))}
                      className={`w-20 text-sm ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}`}
                    />
                  </div>
                </div>
              )}
            </div>
            
            {/* Custom Playbooks */}
            <div className={`rounded-xl p-4 sm:p-6 ${isDarkMode ? 'bg-zinc-900/50 border border-violet-500/20' : 'bg-white border border-violet-200 shadow-sm'}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <BookOpen className={`w-5 h-5 ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`} />
                  <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Custom Playbooks</h3>
                </div>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setShowAddPlaybookDialog(true)}
                  className={isDarkMode ? 'border-white/20 text-white hover:bg-white/10' : ''}
                  data-testid="add-playbook-btn"
                >
                  + Add Playbook
                </Button>
              </div>
              
              <div className="space-y-2">
                {automationConfig.playbooks.map((playbook) => (
                  <div key={playbook.id} className={`p-3 rounded-lg flex items-center justify-between ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${playbook.enabled ? (isDarkMode ? 'bg-violet-500/20' : 'bg-violet-100') : (isDarkMode ? 'bg-zinc-700' : 'bg-slate-200')}`}>
                        <BookOpen className={`w-4 h-4 ${playbook.enabled ? (isDarkMode ? 'text-violet-400' : 'text-violet-600') : (isDarkMode ? 'text-zinc-500' : 'text-slate-400')}`} />
                      </div>
                      <div>
                        <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{playbook.name}</p>
                        <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>{playbook.steps} steps</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        className={isDarkMode ? 'text-indigo-400 hover:bg-indigo-500/10' : 'text-indigo-600 hover:bg-indigo-50'}
                      >
                        <Play className="w-3 h-3" />
                      </Button>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={playbook.enabled}
                          onChange={(e) => {
                            const newPlaybooks = automationConfig.playbooks.map(p => 
                              p.id === playbook.id ? { ...p, enabled: e.target.checked } : p
                            );
                            setAutomationConfig(prev => ({
                              ...prev,
                              playbooks: newPlaybooks
                            }));
                          }}
                          className="sr-only peer"
                        />
                        <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-violet-500"></div>
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>
        
        {/* Marketplace Tab */}
        <TabsContent value="marketplace" className="space-y-4 sm:space-y-6">
          {/* Marketplace Header */}
          <div className={`rounded-xl p-4 sm:p-6 ${isDarkMode ? 'bg-gradient-to-r from-violet-900/30 to-indigo-900/30 border border-violet-500/30' : 'bg-gradient-to-r from-violet-50 to-indigo-50 border border-violet-200'}`}>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${isDarkMode ? 'bg-violet-500/20 border border-violet-500/30' : 'bg-violet-100 border border-violet-200'}`}>
                  <Store className={`w-7 h-7 ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`} />
                </div>
                <div>
                  <h2 className={`text-xl sm:text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Automation Templates Marketplace
                  </h2>
                  <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                    Pre-built automation templates for common security scenarios
                  </p>
                </div>
              </div>
              
              <Select value={marketplaceFilter} onValueChange={setMarketplaceFilter}>
                <SelectTrigger className={`w-48 ${isDarkMode ? 'bg-zinc-900/50 border-white/10 text-white' : 'bg-white border-slate-300'}`}>
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="Compliance">Compliance</SelectItem>
                  <SelectItem value="Incident Response">Incident Response</SelectItem>
                  <SelectItem value="Vulnerability Management">Vulnerability Management</SelectItem>
                  <SelectItem value="SOC Operations">SOC Operations</SelectItem>
                  <SelectItem value="Threat Hunting">Threat Hunting</SelectItem>
                  <SelectItem value="Cloud Security">Cloud Security</SelectItem>
                  <SelectItem value="Identity Security">Identity Security</SelectItem>
                  <SelectItem value="Data Protection">Data Protection</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Templates Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {marketplaceTemplates
              .filter(t => marketplaceFilter === 'all' || t.category === marketplaceFilter)
              .map((template) => {
                const IconComponent = template.icon;
                return (
                  <div 
                    key={template.id}
                    className={`rounded-xl p-4 transition-all cursor-pointer hover:shadow-lg ${isDarkMode ? 'bg-zinc-900/50 border border-white/10 hover:border-violet-500/30' : 'bg-white border border-slate-200 hover:border-violet-300 shadow-sm'}`}
                    onClick={() => setSelectedMarketplaceTemplate(template)}
                    data-testid={`marketplace-template-${template.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-${template.color}-500/20`}>
                        <IconComponent className={`w-5 h-5 text-${template.color}-400`} />
                      </div>
                      {template.verified && (
                        <Badge className="bg-green-500/20 text-green-400 text-xs">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Verified
                        </Badge>
                      )}
                    </div>
                    
                    <h3 className={`font-semibold text-sm mb-1 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {template.name}
                    </h3>
                    <p className={`text-xs mb-3 line-clamp-2 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                      {template.description}
                    </p>
                    
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-3">
                        <span className={`flex items-center gap-1 ${isDarkMode ? 'text-amber-400' : 'text-amber-500'}`}>
                          <Star className="w-3 h-3 fill-current" />
                          {template.rating}
                        </span>
                        <span className={`flex items-center gap-1 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                          <Download className="w-3 h-3" />
                          {template.downloads.toLocaleString()}
                        </span>
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {template.steps} steps
                      </Badge>
                    </div>
                    
                    <div className={`mt-3 pt-3 border-t ${isDarkMode ? 'border-white/10' : 'border-slate-100'}`}>
                      <div className="flex items-center justify-between">
                        <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                          {template.category}
                        </span>
                        {appliedTemplates.some(t => t.id === template.id) && (
                          <Badge className="bg-green-500/20 text-green-400 text-xs">
                            Applied
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
          
          {/* Applied Templates Section */}
          {appliedTemplates.length > 0 && (
            <div className={`rounded-xl p-4 sm:p-6 ${isDarkMode ? 'bg-zinc-900/50 border border-emerald-500/20' : 'bg-white border border-emerald-200 shadow-sm'}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-emerald-500/20 border border-emerald-500/30' : 'bg-emerald-100 border border-emerald-200'}`}>
                    <Package className={`w-5 h-5 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                  </div>
                  <div>
                    <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Applied Templates</h3>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                      {appliedTemplates.length} template{appliedTemplates.length !== 1 ? 's' : ''} currently active
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {appliedTemplates.map((template) => {
                  const IconComponent = template.icon;
                  return (
                    <div 
                      key={template.id}
                      className={`rounded-lg p-3 flex items-center justify-between ${isDarkMode ? 'bg-zinc-800/50 border border-white/5' : 'bg-slate-50 border border-slate-100'}`}
                      data-testid={`applied-template-${template.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center bg-${template.color}-500/20`}>
                          <IconComponent className={`w-4 h-4 text-${template.color}-400`} />
                        </div>
                        <div className="min-w-0">
                          <p className={`text-sm font-medium truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                            {template.name}
                          </p>
                          <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                            Applied {new Date(template.appliedAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeAppliedTemplate(template.id);
                        }}
                        className={`flex-shrink-0 h-8 w-8 p-0 ${isDarkMode ? 'text-red-400 hover:bg-red-500/10' : 'text-red-500 hover:bg-red-50'}`}
                        data-testid={`remove-template-${template.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
      
      {/* Template Preview Dialog */}
      <Dialog open={!!selectedMarketplaceTemplate} onOpenChange={() => setSelectedMarketplaceTemplate(null)}>
        <DialogContent className={`max-w-2xl ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white'}`}>
          {selectedMarketplaceTemplate && (
            <>
              <DialogHeader>
                <DialogTitle className={`flex items-center gap-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-${selectedMarketplaceTemplate.color}-500/20`}>
                    {(() => {
                      const Icon = selectedMarketplaceTemplate.icon;
                      return <Icon className={`w-5 h-5 text-${selectedMarketplaceTemplate.color}-400`} />;
                    })()}
                  </div>
                  {selectedMarketplaceTemplate.name}
                  {selectedMarketplaceTemplate.verified && (
                    <Badge className="bg-green-500/20 text-green-400 text-xs ml-2">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Verified
                    </Badge>
                  )}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <p className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-600'}`}>
                  {selectedMarketplaceTemplate.description}
                </p>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className={`p-3 rounded-lg text-center ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Rating</p>
                    <p className={`text-lg font-bold flex items-center justify-center gap-1 ${isDarkMode ? 'text-amber-400' : 'text-amber-500'}`}>
                      <Star className="w-4 h-4 fill-current" />
                      {selectedMarketplaceTemplate.rating}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg text-center ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Downloads</p>
                    <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {selectedMarketplaceTemplate.downloads.toLocaleString()}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg text-center ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Steps</p>
                    <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {selectedMarketplaceTemplate.steps}
                    </p>
                  </div>
                </div>
                
                <div>
                  <p className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>Tags</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedMarketplaceTemplate.tags.map(tag => (
                      <Badge key={tag} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
                
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                    Author: <span className={isDarkMode ? 'text-white' : 'text-slate-900'}>{selectedMarketplaceTemplate.author}</span>
                  </p>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                    Category: <span className={isDarkMode ? 'text-white' : 'text-slate-900'}>{selectedMarketplaceTemplate.category}</span>
                  </p>
                </div>
              </div>
              
              <DialogFooter>
                <Button
                  variant="ghost"
                  onClick={() => setSelectedMarketplaceTemplate(null)}
                  className={isDarkMode ? 'text-zinc-400 hover:text-white' : ''}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    // Add to applied templates
                    const appliedTemplate = {
                      ...selectedMarketplaceTemplate,
                      appliedAt: new Date().toISOString(),
                      templateId: selectedMarketplaceTemplate.id
                    };
                    setAppliedTemplates(prev => {
                      // Check if already applied
                      if (prev.some(t => t.id === selectedMarketplaceTemplate.id)) {
                        toast.error('This template is already applied');
                        return prev;
                      }
                      return [...prev, appliedTemplate];
                    });
                    
                    // Add to playbooks
                    const newPlaybook = {
                      id: Date.now(),
                      templateId: selectedMarketplaceTemplate.id,
                      name: selectedMarketplaceTemplate.name,
                      steps: selectedMarketplaceTemplate.steps,
                      last_run: null,
                      enabled: true,
                      fromMarketplace: true
                    };
                    setAutomationConfig(prev => ({
                      ...prev,
                      playbooks: [...prev.playbooks, newPlaybook]
                    }));
                    toast.success(`"${selectedMarketplaceTemplate.name}" imported successfully!`);
                    setSelectedMarketplaceTemplate(null);
                  }}
                  className="bg-violet-600 hover:bg-violet-500"
                  data-testid="import-template-btn"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Import Template
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Run Simulation Dialog */}
      <Dialog open={showSimulationDialog} onOpenChange={setShowSimulationDialog}>
        <DialogContent className={isDarkMode ? 'bg-zinc-900 border-white/10' : ''}>
          <DialogHeader>
            <DialogTitle className={isDarkMode ? 'text-white' : ''}>Run BAS Simulation</DialogTitle>
            <DialogDescription>Configure and run a breach and attack simulation</DialogDescription>
          </DialogHeader>
          
          {simulationResult ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className={`font-semibold ${isDarkMode ? 'text-white' : ''}`}>Simulation Complete</h4>
                <Badge className={`${
                  simulationResult.overall_risk === 'critical' ? 'bg-red-500' :
                  simulationResult.overall_risk === 'high' ? 'bg-orange-500' :
                  simulationResult.overall_risk === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                } text-white`}>
                  {simulationResult.overall_risk} risk
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-100'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Techniques Tested</p>
                  <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : ''}`}>{simulationResult.techniques_tested}</p>
                </div>
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-100'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Successful</p>
                  <p className={`text-xl font-bold text-red-500`}>{simulationResult.techniques_successful}</p>
                </div>
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-100'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Controls Effective</p>
                  <p className={`text-xl font-bold text-green-500`}>{simulationResult.controls_effective}/{simulationResult.controls_tested}</p>
                </div>
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-100'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Attack Paths</p>
                  <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : ''}`}>{simulationResult.attack_paths_found}</p>
                </div>
              </div>
              {simulationResult.recommendations?.length > 0 && (
                <div>
                  <h5 className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : ''}`}>Recommendations</h5>
                  <ul className={`text-sm space-y-1 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                    {simulationResult.recommendations.slice(0, 5).map((rec, idx) => (
                      <li key={idx}>• {rec}</li>
                    ))}
                  </ul>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => { setSimulationResult(null); setShowSimulationDialog(false); }}>
                  Close
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <>
              <div className="space-y-4 py-4">
                <div>
                  <Label className={isDarkMode ? 'text-zinc-300' : ''}>Target</Label>
                  <Input
                    value={simulationTarget}
                    onChange={(e) => setSimulationTarget(e.target.value)}
                    placeholder="e.g., api.example.com or 192.168.1.100"
                    className={isDarkMode ? 'bg-zinc-800 border-white/10' : ''}
                  />
                </div>
                <div>
                  <Label className={isDarkMode ? 'text-zinc-300' : ''}>Attack Profile</Label>
                  <Select value={simulationProfile} onValueChange={setSimulationProfile}>
                    <SelectTrigger className={isDarkMode ? 'bg-zinc-800 border-white/10' : ''}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="opportunistic">Opportunistic (Script Kiddie)</SelectItem>
                      <SelectItem value="targeted">Targeted (Skilled Attacker)</SelectItem>
                      <SelectItem value="apt">APT (Nation-State)</SelectItem>
                      <SelectItem value="insider">Insider Threat</SelectItem>
                      <SelectItem value="ransomware">Ransomware Operator</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowSimulationDialog(false)}>Cancel</Button>
                <Button onClick={handleRunSimulation} disabled={runningSimulation}>
                  {runningSimulation ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                  Run Simulation
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Technique Details Modal */}
      <Dialog open={showTechniqueModal} onOpenChange={setShowTechniqueModal}>
        <DialogContent className={`max-w-2xl max-h-[80vh] overflow-y-auto ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-violet-500" />
              {selectedProfile?.name || 'Attack Profile'} - MITRE ATT&CK Techniques
            </DialogTitle>
            <DialogDescription className={isDarkMode ? 'text-zinc-400' : ''}>
              {selectedProfile?.description || 'Techniques associated with this attack profile'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            {selectedProfile?.techniques?.length > 0 ? (
              selectedProfile.techniques.map((techId) => {
                const technique = basTechniques[techId];
                return technique ? (
                  <div 
                    key={techId}
                    onClick={() => setSelectedTechnique(selectedTechnique === techId ? null : techId)}
                    className={`p-3 rounded-lg cursor-pointer transition-all ${
                      selectedTechnique === techId 
                        ? isDarkMode ? 'bg-violet-500/20 border border-violet-500/30' : 'bg-violet-50 border border-violet-200'
                        : isDarkMode ? 'bg-zinc-800 hover:bg-zinc-700' : 'bg-slate-50 hover:bg-slate-100'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`font-mono text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-violet-500/20 text-violet-400' : 'bg-violet-100 text-violet-600'}`}>
                          {techId}
                        </span>
                        <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{technique.name}</span>
                      </div>
                      <Badge className="text-xs capitalize">{technique.tactic?.replace('_', ' ')}</Badge>
                    </div>
                    {selectedTechnique === techId && (
                      <div className="mt-3 space-y-2 pt-3 border-t border-white/10">
                        <p className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-600'}`}>{technique.description}</p>
                        {technique.detection?.length > 0 && (
                          <div>
                            <p className={`text-xs font-medium ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Detection Methods:</p>
                            <ul className="text-xs mt-1 space-y-1">
                              {technique.detection.map((d, i) => (
                                <li key={i} className={isDarkMode ? 'text-zinc-400' : 'text-slate-500'}>• {d}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {technique.mitigations?.length > 0 && (
                          <div>
                            <p className={`text-xs font-medium ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Mitigations:</p>
                            <ul className="text-xs mt-1 space-y-1">
                              {technique.mitigations.map((m, i) => (
                                <li key={i} className={isDarkMode ? 'text-zinc-400' : 'text-slate-500'}>• {m}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div key={techId} className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-50'}`}>
                    <span className="font-mono text-xs">{techId}</span>
                    <span className={`ml-2 text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Technique details not available</span>
                  </div>
                );
              })
            ) : (
              <p className={`text-center py-8 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                No techniques available for this profile
              </p>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowTechniqueModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Create Campaign Dialog */}
      <Dialog open={showCampaignDialog} onOpenChange={setShowCampaignDialog}>
        <DialogContent className={`max-w-lg ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-violet-500" />
              Create Threat Campaign
            </DialogTitle>
            <DialogDescription className={isDarkMode ? 'text-zinc-400' : ''}>
              Configure a custom BAS campaign with specific techniques and targets
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className={isDarkMode ? 'text-zinc-300' : ''}>Campaign Name</Label>
              <Input
                value={newCampaign.name}
                onChange={(e) => setNewCampaign({...newCampaign, name: e.target.value})}
                placeholder="e.g., Q1 Ransomware Assessment"
                className={`mt-1 ${isDarkMode ? 'bg-zinc-800 border-white/10' : ''}`}
              />
            </div>
            <div>
              <Label className={isDarkMode ? 'text-zinc-300' : ''}>Description</Label>
              <Input
                value={newCampaign.description}
                onChange={(e) => setNewCampaign({...newCampaign, description: e.target.value})}
                placeholder="Brief description of the campaign"
                className={`mt-1 ${isDarkMode ? 'bg-zinc-800 border-white/10' : ''}`}
              />
            </div>
            <div>
              <Label className={isDarkMode ? 'text-zinc-300' : ''}>Base Profile</Label>
              <Select value={newCampaign.profile} onValueChange={(v) => setNewCampaign({...newCampaign, profile: v})}>
                <SelectTrigger className={`mt-1 ${isDarkMode ? 'bg-zinc-800 border-white/10' : ''}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="opportunistic">Opportunistic</SelectItem>
                  <SelectItem value="targeted">Targeted</SelectItem>
                  <SelectItem value="apt">APT</SelectItem>
                  <SelectItem value="insider">Insider</SelectItem>
                  <SelectItem value="ransomware">Ransomware</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className={isDarkMode ? 'text-zinc-300' : ''}>Targets (comma-separated)</Label>
              <Input
                value={newCampaign.targets}
                onChange={(e) => setNewCampaign({...newCampaign, targets: e.target.value})}
                placeholder="e.g., api.example.com, 192.168.1.100"
                className={`mt-1 ${isDarkMode ? 'bg-zinc-800 border-white/10' : ''}`}
              />
            </div>
            <div>
              <Label className={isDarkMode ? 'text-zinc-300' : ''}>Schedule</Label>
              <Select value={newCampaign.schedule} onValueChange={(v) => setNewCampaign({...newCampaign, schedule: v})}>
                <SelectTrigger className={`mt-1 ${isDarkMode ? 'bg-zinc-800 border-white/10' : ''}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual Trigger</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className={isDarkMode ? 'text-zinc-300' : ''}>Select Techniques</Label>
              <div className={`mt-2 max-h-32 overflow-y-auto p-2 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                {Object.entries(basTechniques).slice(0, 15).map(([techId, tech]) => (
                  <label key={techId} className="flex items-center gap-2 p-1 cursor-pointer hover:bg-white/5 rounded">
                    <input
                      type="checkbox"
                      checked={newCampaign.techniques.includes(techId)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNewCampaign({...newCampaign, techniques: [...newCampaign.techniques, techId]});
                        } else {
                          setNewCampaign({...newCampaign, techniques: newCampaign.techniques.filter(t => t !== techId)});
                        }
                      }}
                      className="rounded"
                    />
                    <span className="font-mono text-xs">{techId}</span>
                    <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>{tech.name}</span>
                  </label>
                ))}
              </div>
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                {newCampaign.techniques.length} techniques selected
              </p>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowCampaignDialog(false)}>Cancel</Button>
            <Button 
              onClick={() => {
                if (newCampaign.name && newCampaign.targets) {
                  setCustomCampaigns([...customCampaigns, {...newCampaign}]);
                  setNewCampaign({
                    name: '',
                    description: '',
                    profile: 'targeted',
                    techniques: [],
                    targets: '',
                    schedule: 'manual',
                    notify_on_complete: true
                  });
                  setShowCampaignDialog(false);
                  toast.success('Campaign created successfully');
                } else {
                  toast.error('Please fill in campaign name and targets');
                }
              }}
            >
              Create Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusCard({ title, value, icon, color, isDarkMode }) {
  const colorClasses = {
    green: 'text-green-500 bg-green-500/10',
    red: 'text-red-500 bg-red-500/10',
    violet: 'text-violet-500 bg-violet-500/10',
    blue: 'text-blue-500 bg-blue-500/10',
    orange: 'text-orange-500 bg-orange-500/10'
  };
  
  return (
    <div className={`rounded-xl p-3 sm:p-4 ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-white border border-slate-200 shadow-sm'}`}>
      <div className="flex items-center justify-between mb-1 sm:mb-2">
        <span className={`text-xs sm:text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>{title}</span>
        <div className={`p-1 sm:p-1.5 rounded-lg ${colorClasses[color]}`}>
          {icon}
        </div>
      </div>
      <p className={`text-lg sm:text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{value}</p>
    </div>
  );
}

function IntegrationMetric({ label, value, trend, isDarkMode, onClick }) {
  return (
    <button 
      onClick={onClick}
      className={`p-2 sm:p-3 rounded-lg text-left transition-all ${
        onClick 
          ? isDarkMode 
            ? 'hover:bg-white/5 cursor-pointer' 
            : 'hover:bg-white/50 cursor-pointer'
          : ''
      } ${isDarkMode ? 'bg-white/5' : 'bg-white/60'}`}
    >
      <div className="flex items-center justify-between mb-1">
        <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>{label}</span>
        {trend && (
          trend === 'up' ? (
            <TrendingUp className="w-3 h-3 text-red-500" />
          ) : (
            <TrendingDown className="w-3 h-3 text-green-500" />
          )
        )}
      </div>
      <p className={`text-base sm:text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{value}</p>
    </button>
  );
}
